import useMemoObject from '@dyl/hooks/useMemoObject';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { localStorageEncrypt } from '../../utils/localStorageEncrypt';

/**
 * @typedef Settings
 * @property {'always' | 'only-larger-screens' | 'never'} shouldOpenSidebarAtStartup
 * @property {boolean} shouldShowImagePreviewsByDefault
 * @property {boolean} shouldAutoHideSidebarOnScreenShrink
 * @property {string} startupPath
 */

/**
 * @typedef SettingsUtils
 * @property {(diff: Partial<Settings>) => void} update
 * @property {() => void} sync
 */

const keyPrefix = 'dyl_settings_';
const keyPattern = new RegExp(`^${keyPrefix}(.+)$`, 'i');

const parseBoolean = raw => (
    raw === 'true' ? true
    : raw === 'false' ? false
    : undefined
)

/** @type {Partial<{ [P in keyof Settings]: (raw: string) => Settings[P] }>} */
const parsers = {
    shouldShowImagePreviewsByDefault: parseBoolean,
    shouldAutoHideSidebarOnScreenShrink: parseBoolean,
};

function getFromLocalStorage() {
    /** @type {Partial<Settings>} */
    const obj = {};

    for (let i = 0; i < localStorage.length; i++) {
        const [fullKey, key] = keyPattern.exec(localStorage.key(i)) || [];

        if (!key) {
            continue;
        }

        let value = localStorage.getItem(fullKey);
        if (key in parsers) {
            value = parsers[key](value);
        }

        obj[key] = value;
    }

    return obj;
};

/** @returns {Settings} */
function getDefaults() {
    return {
        // First define internal defaults...
        shouldOpenSidebarAtStartup: 'only-larger-screens',
        shouldShowImagePreviewsByDefault: false,
        shouldAutoHideSidebarOnScreenShrink: true,
        levelSelectPath: '/[level_label]/welcome',
        startupPath: '/Pre-A1/welcome',

        // Then override with user's choices
        ...getFromLocalStorage(),
    };
}

/**
 * @type {React.Context<Settings & SettingsUtils>}
 */
const SettingsContext = createContext();

export default function SettingsManager({ children }) {
    const [settings, setSettings] = useState(getDefaults);

    const update = useCallback(async diff => {
        for (const p in diff) {
            localStorage.setItem(`${keyPrefix}${p}`, await localStorageEncrypt(diff[p]));
        }

        setSettings(prev => ({
            ...prev,
            ...diff,
        }));
    }, []);

    const sync = useCallback(() => {
        update(getFromLocalStorage());
    }, [update]);

    useEffect(() => {
        window.addEventListener('focus', sync);

        return () => window.removeEventListener('focus', sync);
    }, []);

    const context = useMemoObject({
        ...settings,
        update,
        sync,
    });

    return (
        <SettingsContext.Provider value={context}>
            {children}
        </SettingsContext.Provider>
    );
}

export function useSettings() {
    return useContext(SettingsContext);
}